import { memo } from 'react';
import { G, Path, Text } from 'react-native-svg';
import { plotY } from './ComboAxes';
import { labelInside, textWidth } from './labels';
import { ComboLayout, isStacked } from './layout';
import { barPath } from './paths';
import { ComboBarStyle, ComboSeries, ComboTheme } from './types';

/** A bar column: one series, or the whole stack of a stacked chart. */
type Column = { kind: 'stack'; series: ComboSeries[] } | ComboSeries;

const kBarGap = 2;

function columnsOf(series: ComboSeries[], stacked: boolean): Column[] {
  const bars = series.filter(entry => entry.type === 'bar');
  const stack = bars.filter(entry => isStacked(entry, stacked));
  const rest = bars.filter(entry => !isStacked(entry, stacked));
  return [
    ...(stack.length ? [{ kind: 'stack', series: stack } as const] : []),
    ...rest,
  ];
}

/**
 * Every bar series, side by side in each category's band; on a stacked
 * chart the left bars stack into one column.
 */
export const ComboBars = memo(
  ({
    layout,
    series,
    stacked,
    bar,
    dataLabels,
    compact,
    theme,
  }: {
    layout: ComboLayout;
    series: ComboSeries[];
    stacked: boolean;
    bar: ComboBarStyle;
    dataLabels: boolean;
    /** A value short, for a stacked label too wide for its bar. */
    compact: (value: number) => string;
    theme: ComboTheme;
  }) => {
    const columns = columnsOf(series, stacked);
    if (!columns.length) return null;
    const { band, plot } = layout;
    const group = band * (1 - bar.gap);
    const width = Math.max(
      (group - kBarGap * (columns.length - 1)) / columns.length,
      1,
    );
    const f = theme.fontSize;
    const marks: JSX.Element[] = [];
    const labels: JSX.Element[] = [];
    const label = (key: string, text: string, x: number, y: number) => {
      if (textWidth(text, f) > band * layout.step + 2 && columns.length > 1)
        return;
      labels.push(
        <Text
          key={key}
          x={x}
          y={y}
          fontSize={f}
          fontFamily={theme.fontFamily}
          fill={theme.label}
          textAnchor="middle"
        >
          {text}
        </Text>,
      );
    };
    columns.forEach((column, c) => {
      const scaleOf = (entry: ComboSeries) =>
        entry.axis === 'right' ? layout.right : layout.left;
      layout.labels.forEach((_, i) => {
        const x =
          plot.left + band * i + (band - group) / 2 + c * (width + kBarGap);
        const center = x + width / 2;
        const showLabel = dataLabels && i % layout.step === 0;
        if ('kind' in column) {
          const scale = layout.left;
          if (!scale) return;
          let up = 0;
          let down = 0;
          const top = column.series.reduce(
            (last, entry, s) => ((entry.values[i] ?? 0) > 0 ? s : last),
            -1,
          );
          column.series.forEach((entry, s) => {
            const value = entry.values[i];
            if (value === undefined || value === 0) return;
            const from = value >= 0 ? up : down;
            const to = from + value;
            if (value >= 0) up = to;
            else down = to;
            const y0 = plotY(layout, scale, from);
            const y1 = plotY(layout, scale, to);
            marks.push(
              <Path
                key={`${entry.key}-${i}`}
                d={barPath(x, width, y0, y1, s === top ? bar.cornerRadius : 0)}
                fill={entry.color}
              />,
            );
            const text = showLabel
              ? labelInside(entry.labels?.[i], value, width, f, compact)
              : undefined;
            if (text && Math.abs(y0 - y1) > f + 2)
              labels.push(
                <Text
                  key={`l-${entry.key}-${i}`}
                  x={center}
                  y={(y0 + y1) / 2 + f * 0.35}
                  fontSize={f}
                  fontFamily={theme.fontFamily}
                  fill="#FFFFFF"
                  textAnchor="middle"
                >
                  {text}
                </Text>,
              );
          });
          return;
        }
        const scale = scaleOf(column);
        const value = column.values[i];
        if (!scale || value === undefined) return;
        const base = Math.min(Math.max(0, scale.min), scale.max);
        const y0 = plotY(layout, scale, base);
        const y1 = plotY(layout, scale, value);
        marks.push(
          <Path
            key={`${column.key}-${i}`}
            d={barPath(x, width, y0, y1, bar.cornerRadius)}
            fill={column.color}
          />,
        );
        const text = column.labels?.[i];
        if (showLabel && text)
          label(
            `l-${column.key}-${i}`,
            text,
            center,
            value >= 0 ? y1 - 4 : y1 + f + 2,
          );
      });
    });
    return (
      <G>
        {marks}
        {labels}
      </G>
    );
  },
);
